import { useEffect } from 'react';

import { MemberToolButton } from './components/MemberToolButton';
import { MemberTablePageButton } from './components/MemberTablePageButton';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { push, useRouteMatch } from '../../app/route';
import {
  selectCurrentTenantId,
} from '../user/userSlice';
import {
  selectListMembers,
  selectListMemberPage,
  listMembersAction,
  clearListMemberPageAction,
  deleteMemberAction,
  updateMemberAction,
  getMemberListUrl,
  getMemberCreateUrl,
  getMemberDetailUrl,
  displayEmailState,
} from './memberSlice';

type PathParams = {
  tenant_id: string;
};

export function MemberListPage() {
  const dispatch = useAppDispatch();
  const members = useAppSelector(selectListMembers);
  const tenant_id = useAppSelector(selectCurrentTenantId);
  const match = useRouteMatch<PathParams>();
  const page = useAppSelector(selectListMemberPage);

  useEffect(() => {
    if (match.params.tenant_id !== tenant_id) {
      dispatch(push(getMemberListUrl(tenant_id)));
    }
  }, [tenant_id]);

  useEffect(() => {
    dispatch(listMembersAction({ tenant_id, page: page.page, pre_page: page.pre_page }));
    return () => {
      dispatch(clearListMemberPageAction());
    }
  }, [tenant_id, page.page, page.pre_page, dispatch]);

  const goDetail = (mode: 'view'|'edit', client_name: string) => {
    dispatch(push(getMemberDetailUrl(tenant_id, client_name, mode)));
  };

  const goCreate = () => {
    dispatch(push(getMemberCreateUrl(tenant_id)));
  };

  const handleDeleteMember = async(user_id: string) => {
    let result = await dispatch(deleteMemberAction({
      tenant_id,
      user_id
    }));
    if (deleteMemberAction.fulfilled.match(result)) {
      dispatch(listMembersAction({ tenant_id, page: page.page, pre_page: page.pre_page }));
    }
  };

  const handleEnableMember = async(user_id: string) => {
    let result = await dispatch(updateMemberAction({
      tenant_id,
      user_id,
      body: { state: 'enabled' },
    }));
    if (updateMemberAction.fulfilled.match(result)) {
      dispatch(listMembersAction({ tenant_id, page: page.page, pre_page: page.pre_page }));
    }
  };

  const handleDisableMember = async(user_id: string) => {
    let result = await dispatch(updateMemberAction({
      tenant_id,
      user_id,
      body: { state: 'disabled' },
    }));
    if (updateMemberAction.fulfilled.match(result)) {
      dispatch(listMembersAction({ tenant_id, page: page.page, pre_page: page.pre_page }));
    }
  };

  const handlePageChange = (pageNumber: number) => {
    dispatch(listMembersAction({ tenant_id, page: pageNumber, pre_page: page.pre_page }));
  };

  return (
    <div className="container">
      <div className="d-flex justify-content-between py-4">
        <h1>Member</h1>
        <button className="btn btn-primary my-2" onClick={() => goCreate()}>
          Create
        </button>
      </div>
      <table className="table">
        <thead className="table-dark">
          <tr>
            <th scope="col">#</th>
            <th scope="col">TenantId</th>
            <th scope="col">UserId</th>
            <th scope="col">Name</th>
            <th scope="col">Username</th>
            <th scope="col">Email</th>
            <th scope="col">Email State</th>
            <th scope="col">State</th>
            <th scope="col">Action</th>
          </tr>
        </thead>
        <tbody>
          {members.map((member, index) => {
            return (
              <tr key={index}>
                <th scope="row">{ index+1 }</th>
                <td>{member.tenant_id}</td>
                <td>{member.id}</td>
                <td>{member.name}</td>
                <td>{member.username}</td>
                <td>{member.email}</td>
                <td>{displayEmailState(member)}</td>
                <td>{member.state}</td>
                <td>
                  <MemberToolButton id={member.id}
                    onClickEnable={() => handleEnableMember(member.id)}
                    onClickDisable={() => handleDisableMember(member.id)}
                    onClickEdit={() => goDetail('edit', member.id)}
                    onClickView={() => goDetail('view', member.id)}
                    onClickDelete={() => handleDeleteMember(member.id)}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="row d-flex">
        <MemberTablePageButton pageNumber={page.page} prePage={page.pre_page}
          totalCount={page.total_count} onClick={handlePageChange}
        />
      </div>
    </div>
  );
}
